import { Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { BranchSheetFromParams } from '@/components/Branches/BranchSheetFromParams';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import { NewAssetAction } from '@/components/LibraryTable/NewAssetAction';
import { SlocWarning } from '@/components/LibraryTable/SlocWarning';
import { SourcesNav } from '@/components/Sources/SourcesNav';
import { useIsOrgOrSourceAdmin } from '@/hooks/useIsOrgOrSourceAdmin';
import { searchParam } from '@/utils/router';

// Per-route header configuration. Child routes declare their own header via
// `staticData`; the layout reads the deepest match that provides a navbarTitle
// (the same pattern LayoutContext uses to read route staticData).
export interface DashboardStaticData {
  navbarTitle?: string;
}

const dashboardSearchSchema = z.object({
  branch: searchParam().optional(),
  branchCodebase: searchParam().optional(),
});

// Pathless layout route — the `_dashboard` segment is stripped from URLs, so
// children resolve at /$org/sources and /$org/branches. It
// provides the shared shell (LayoutDefault chrome + SourcesNav) and renders the
// active child via <Outlet/>.
export const Route = createFileRoute('/$organizationName/_dashboard')({
  component: DashboardLayout,
  validateSearch: zodValidator(dashboardSearchSchema),
});

function DashboardLayout() {
  const { branch, branchCodebase } = Route.useSearch();
  const navigate = useNavigate();
  // Users who can't manage sources get a read-only view: no add-source or
  // add-SLOC actions in the header.
  const isOrgOrSourceAdmin = useIsOrgOrSourceAdmin();

  const header = useRouterState({
    select: (s) =>
      (s.matches.findLast((m) => (m.staticData as DashboardStaticData)?.navbarTitle)?.staticData ??
        {}) as DashboardStaticData,
  });

  return (
    <>
      <LayoutDefault
        showBorder
        navbarTitle={header.navbarTitle}
        header={
          <LayoutHeader
            right={
              isOrgOrSourceAdmin ? (
                <div className="flex items-center gap-2.5">
                  <SlocWarning />
                  <NewAssetAction />
                </div>
              ) : undefined
            }
          >
            <LayoutHeaderTitle title={header.navbarTitle ?? ''} />
          </LayoutHeader>
        }
      >
        <div className="space-y-4 px-4 pt-4 pb-12">
          <SourcesNav />
          <Outlet />
        </div>
      </LayoutDefault>
      <BranchSheetFromParams
        branch={branch}
        branchCodebase={branchCodebase}
        onClose={() =>
          navigate({ to: '.', search: (prev) => ({ ...prev, branch: undefined, branchCodebase: undefined }) })
        }
      />
    </>
  );
}
